import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, BookOpen, X, FileText, Image as ImageIcon, Headphones, ArrowLeft, Video, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { uploadFileInChunks as uploadFileInChunksShared } from '../utils/chunkedUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const UploadBook = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiCategories, setAiCategories] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    genres: '',
    pages: '',
    publishYear: '',
    amazonLink: '',
    barnesNobleLink: '',
    authorBio: '',
    seriesName: '',
    seriesOrder: '',
  });
  const [coverImage, setCoverImage] = useState(null);
  const [bookFile, setBookFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [authorPhoto, setAuthorPhoto] = useState(null);
  const [authorPhotoPreview, setAuthorPhotoPreview] = useState(null);
  
  // Trailer state
  const [trailerFile, setTrailerFile] = useState(null);
  const [trailerDuration, setTrailerDuration] = useState(0);
  const [trailerError, setTrailerError] = useState('');
  const [trailerPreview, setTrailerPreview] = useState(null);
  const [trailerExternalUrl, setTrailerExternalUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const videoInputRef = useRef(null);

  // Orphan-chat detection — set after upload if a previous incarnation of
  // this book has stranded discussion messages we could re-attach.
  const [orphanPrompt, setOrphanPrompt] = useState(null);  // {bookId, match, busy}

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBookFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'application/epub+zip')) {
      setBookFile(file);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please upload a PDF or EPUB file.',
        variant: 'destructive',
      });
    }
  };

  // Handle trailer file selection and duration validation
  const handleTrailerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setTrailerError('');
    setTrailerPreview(null);
    
    // Validate file type
    if (!['video/mp4', 'video/webm'].includes(file.type)) {
      setTrailerError('Invalid file format. Only MP4 and WebM are accepted.');
      return;
    }

    // Validate file size (250MB max)
    if (file.size > 250 * 1024 * 1024) {
      setTrailerError('File size exceeds 250MB limit.');
      return;
    }

    // Get video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      
      if (duration > 150) {
        setTrailerError('Trailer must not exceed 2 minutes and 30 seconds (150 seconds).');
        setTrailerFile(null);
        setTrailerPreview(null);
        window.URL.revokeObjectURL(video.src);
        return;
      }
      
      setTrailerDuration(duration);
      setTrailerFile(file);
      setTrailerPreview(video.src);
    };
    video.onerror = () => {
      setTrailerError('Could not read video file. Please try another file.');
    };
    video.src = URL.createObjectURL(file);
  };

  const clearTrailer = () => {
    setTrailerFile(null);
    setTrailerDuration(0);
    setTrailerError('');
    if (trailerPreview) {
      URL.revokeObjectURL(trailerPreview);
      setTrailerPreview(null);
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleAICategorization = async () => {
    if (!formData.title || !formData.author || !formData.description) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in title, author, and description first.',
        variant: 'destructive',
      });
      return;
    }

    setAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/books/categorize`,
        {
          title: formData.title,
          author: formData.author,
          description: formData.description,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const categorization = response.data.categorization;
        setAiCategories(categorization);
        
        // Auto-fill genres field
        const allGenres = [
          categorization.primary_genre,
          ...categorization.secondary_genres
        ].join(', ');
        setFormData({
          ...formData,
          genres: allGenres
        });

        toast({
          title: 'AI Analysis Complete!',
          description: 'Book has been automatically categorized.',
        });
      }
    } catch (error) {
      console.error('AI categorization error:', error);
      toast({
        title: 'Analysis Failed',
        description: error.response?.data?.detail || 'Could not analyze book. You can still manually enter genres.',
        variant: 'destructive',
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Chunked upload for large files (book PDFs / audiobooks) — shared util.
  const uploadFileInChunks = (file, fileType, bookId, token) =>
    uploadFileInChunksShared(file, {
      fileType,
      bookId,
      token,
      label: 'book file',
      onProgress: (pct, text) => { setUploadProgress(pct); setUploadStatus(text); },
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!coverImage) {
      toast({
        title: 'Cover Image Required',
        description: 'Please upload a cover image for your book.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!bookFile) {
      toast({
        title: 'Book File Required',
        description: 'Please upload the book PDF/EPUB file.',
        variant: 'destructive',
      });
      return;
    }

    // Check trailer error
    if (trailerError) {
      toast({
        title: 'Trailer Error',
        description: trailerError,
        variant: 'destructive',
      });
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast({
          title: 'Authentication Error',
          description: 'Please login again.',
          variant: 'destructive',
        });
        navigate('/login');
        return;
      }
      
      const formDataToSend = new FormData();
      
      formDataToSend.append('title', formData.title);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('genres', formData.genres);
      formDataToSend.append('pages', formData.pages);
      formDataToSend.append('publish_year', formData.publishYear);
      if (formData.amazonLink) formDataToSend.append('amazon_link', formData.amazonLink);
      if (formData.barnesNobleLink) formDataToSend.append('barnes_noble_link', formData.barnesNobleLink);
      if (formData.authorBio) formDataToSend.append('author_bio', formData.authorBio);
      if (formData.seriesName) formDataToSend.append('series_name', formData.seriesName);
      if (formData.seriesOrder) formDataToSend.append('series_order', formData.seriesOrder);
      if (authorPhoto) formDataToSend.append('author_photo', authorPhoto);
      formDataToSend.append('cover_image', coverImage);
      
      if (bookFile) {
        // Large book files (>=10MB) are uploaded separately in chunks
        // after book creation — a single giant multipart POST gets
        // dropped by the proxy/origin in production (Cloudflare 520).
        if (bookFile.size < 10 * 1024 * 1024) {
          formDataToSend.append('book_file', bookFile);
        }
      }

      // For small trailers (< 10MB), include with book upload
      // For large trailers, upload separately after book creation
      const hasLargeTrailer = trailerFile && trailerFile.size >= 10 * 1024 * 1024;
      
      if (trailerFile && !hasLargeTrailer) {
        formDataToSend.append('trailer_file', trailerFile);
        formDataToSend.append('trailer_duration', trailerDuration.toString());
      }

      // Add external trailer URL (YouTube/Facebook)
      if (trailerExternalUrl.trim()) {
        formDataToSend.append('trailer_external_url', trailerExternalUrl.trim());
      }

      console.log('Uploading book with data:', {
        title: formData.title,
        author: formData.author,
        hasToken: !!token,
        hasCover: !!coverImage,
        hasBook: !!bookFile,
        hasTrailer: !!trailerFile && !hasLargeTrailer,
        hasLargeTrailer: hasLargeTrailer,
        trailerDuration: trailerDuration
      });

      const response = await axios.post(
        `${API}/books/upload`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000 // 3 minutes for book upload
        }
      );

      console.log('Upload response:', response.data);
      const bookId = response.data.book_id;

      // Large book file → chunked upload (attached server-side on assembly)
      if (bookFile && bookFile.size >= 10 * 1024 * 1024 && bookId) {
        setUploadStatus('Uploading book file in parts...');
        setUploadProgress(0);
        toast({
          title: 'Book created!',
          description: 'Now uploading the book file in parts...',
        });
        await uploadFileInChunks(bookFile, 'book', bookId, token);
      }

      // If there's a large trailer, upload it separately using chunked upload
      if (hasLargeTrailer && bookId) {
        setUploadStatus('Preparing trailer upload...');
        setUploadProgress(0);
        
        toast({
          title: 'Book uploaded!',
          description: 'Now uploading trailer file in parts...',
        });
        
        // Use 1MB chunks - balance between compatibility and assembly speed
        const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks
        const fileSize = trailerFile.size;
        const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
        
        console.log(`Starting chunked trailer upload: ${(fileSize / (1024 * 1024)).toFixed(2)}MB in ${totalChunks} parts of 1MB`);
        
        // Initialize chunked upload
        setUploadStatus(`Initializing upload (${totalChunks} parts)...`);
        const initResponse = await axios.post(`${API}/upload/init`, null, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            filename: trailerFile.name,
            file_size: fileSize,
            file_type: 'trailer',
            content_type: trailerFile.type || 'video/mp4',
            total_chunks: totalChunks,
            book_id: bookId,
            duration: trailerDuration
          },
          timeout: 30000
        });
        
        const uploadId = initResponse.data.upload_id;
        
        // Helper function to upload chunk with retry
        const uploadChunkWithRetry = async (chunkIndex, chunk, maxRetries = 3) => {
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const chunkFormData = new FormData();
              chunkFormData.append('chunk', chunk, `chunk_${chunkIndex}`);
              
              await axios.post(`${API}/upload/chunk/${uploadId}/${chunkIndex}`, chunkFormData, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                },
                timeout: 120000, // 2 minutes per chunk
                maxContentLength: Infinity,
                maxBodyLength: Infinity
              });
              return true;
            } catch (error) {
              console.warn(`Chunk ${chunkIndex} attempt ${attempt}/${maxRetries} failed:`, error.message);
              if (attempt < maxRetries) {
                const delay = 2000 * attempt;
                console.log(`Waiting ${delay}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              } else {
                throw error;
              }
            }
          }
        };
        
        // Upload chunks with retry and progress tracking
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, fileSize);
          const chunk = trailerFile.slice(start, end);
          
          const progress = Math.round((i / totalChunks) * 90);
          setUploadProgress(progress);
          setUploadStatus(`Uploading trailer part ${i + 1} of ${totalChunks}... (${(chunk.size / 1024).toFixed(0)}KB)`);
          
          await uploadChunkWithRetry(i, chunk, 3);
          console.log(`✓ Trailer chunk ${i + 1}/${totalChunks} uploaded`);
        }
        
        // Start assembly (returns immediately, processes in background)
        setUploadProgress(92);
        setUploadStatus('Starting video assembly...');
        
        try {
          await axios.post(`${API}/upload/complete/${uploadId}`, null, {
            headers: { 'Authorization': `Bearer ${token}` },
            timeout: 30000
          });
        } catch (completeError) {
          // Timeout is okay - background is still processing
          if (completeError.code !== 'ECONNABORTED' && !completeError.message?.includes('timeout')) {
            throw completeError;
          }
        }
        
        // Poll for completion - 10 minutes for large files
        setUploadProgress(95);
        setUploadStatus('Assembling trailer (this may take a few minutes)...');
        
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          try {
            const statusResponse = await axios.get(`${API}/upload/status/${uploadId}`, {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 10000
            });
            
            if (statusResponse.data.status === 'completed') {
              break;
            } else if (statusResponse.data.status === 'failed') {
              throw new Error(statusResponse.data.error || 'Assembly failed');
            }
            
            const elapsed = (attempts + 1) * 5;
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
            setUploadStatus(`Assembling trailer... (${timeStr})`);
          } catch (statusError) {
            console.warn('Status check error:', statusError.message);
          }
          
          attempts++;
        }
        
        if (attempts >= maxAttempts) {
          throw new Error('Assembly timed out after 10 minutes. Refresh the page - the trailer may still be processing.');
        }
        
        setUploadProgress(100);
        setUploadStatus('✓ Complete!');
        console.log('Large trailer upload completed successfully');
      }

      toast({
        title: 'Upload Complete!',
        description: trailerFile ? 'Your book and trailer are now available.' : 'Your book is now available.',
      });

      // FEAT: if the upload response surfaced an orphaned chat room from a
      // previous incarnation of this book, prompt before redirecting.
      const orphanMatch = response.data?.orphan_chat_match;
      if (orphanMatch && bookId) {
        setOrphanPrompt({ bookId, match: orphanMatch, busy: false });
        setLoading(false);
        return;
      }

      // Force reload to refresh book list
      window.location.href = '/';
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to upload. Please try again.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again with a smaller file or check your connection.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please try a smaller trailer file.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Reset upload progress on error
      setUploadProgress(0);
      setUploadStatus('');
      
      toast({
        title: 'Upload Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Decide whether to rebind the orphaned chat room to the just-uploaded book.
  const restoreOrphanChats = async (decision) => {
    if (!orphanPrompt) return;
    const { bookId, match } = orphanPrompt;
    if (decision === 'skip') {
      setOrphanPrompt(null);
      window.location.href = '/';
      return;
    }
    setOrphanPrompt({ ...orphanPrompt, busy: true });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/chat/admin/rebind-room`,
        { from_room: match.room, to_room: bookId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: `Restored ${res.data?.moved ?? match.message_count} chat message${(res.data?.moved ?? match.message_count) === 1 ? '' : 's'}`,
        description: 'Your readers will see the discussion when they open the book.',
      });
    } catch (err) {
      toast({
        title: 'Could not restore chats',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setOrphanPrompt(null);
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-20 pb-12">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-yellow-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-red-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
      </div>

      {/* Orphaned-chat restore prompt — rendered when a previous incarnation
          of this book has stranded discussion messages. */}
      {orphanPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          data-testid="orphan-chat-prompt"
        >
          <div className="bg-zinc-900 border border-fuchsia-500/40 rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                <span className="text-fuchsia-300 text-xl">💬</span>
              </div>
              <h3 className="text-xl font-bold text-white">Restore previous chat history?</h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              We found <span className="font-semibold text-fuchsia-300">{orphanPrompt.match.message_count} chat message{orphanPrompt.match.message_count === 1 ? '' : 's'}</span> from a previous version of this book.
              {orphanPrompt.match.sample_authors?.length > 0 && (
                <> Posted by <span className="text-gray-200">{orphanPrompt.match.sample_authors.slice(0, 3).join(', ')}</span>{orphanPrompt.match.sample_authors.length > 3 ? ' and others' : ''}.</>
              )}
            </p>
            <p className="text-gray-400 text-xs mb-6">
              Restoring will move them into the new book&apos;s discussion so readers see the original conversation. This action is reversible from <span className="text-fuchsia-300">Admin Settings → Chat Recovery</span>.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={orphanPrompt.busy}
                onClick={() => restoreOrphanChats('skip')}
                className="px-4 py-2 rounded-lg text-gray-300 hover:bg-white/5 transition-colors text-sm disabled:opacity-50"
                data-testid="orphan-chat-skip"
              >
                Skip
              </button>
              <button
                type="button"
                disabled={orphanPrompt.busy}
                onClick={() => restoreOrphanChats('restore')}
                className="px-4 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-medium transition-colors text-sm disabled:opacity-50"
                data-testid="orphan-chat-restore"
              >
                {orphanPrompt.busy ? 'Restoring…' : 'Restore chats'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-300 transition-colors mb-4 inline-flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-yellow-600/20 rounded-xl">
              <BookOpen className="h-10 w-10 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Upload Book</h1>
              <p className="text-gray-400">Add a new book with PDF/EPUB, cover art, and optional trailer</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-2 mt-6">
            <Button
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Upload Book
            </Button>
            <Button
              onClick={() => navigate('/upload-audiobook')}
              variant="outline"
              className="border-white/20 text-gray-400 hover:text-white hover:bg-white/10"
            >
              <Headphones className="h-4 w-4 mr-2" />
              Upload Audiobook
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-lg p-8 space-y-6 border border-white/10">
          {/* Book Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-white text-lg">
              Book Title *
            </Label>
            <Input
              id="title"
              name="title"
              type="text"
              placeholder="Enter your book title"
              value={formData.title}
              onChange={handleInputChange}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
              required
            />
          </div>

          {/* Author */}
          <div className="space-y-2">
            <Label htmlFor="author" className="text-white text-lg">
              Author Name *
            </Label>
            <Input
              id="author"
              name="author"
              type="text"
              placeholder="Enter author name"
              value={formData.author}
              onChange={handleInputChange}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white text-lg">
              Description *
            </Label>
            <Textarea
              id="description"
              name="description"
              placeholder="Provide a compelling description of your book..."
              value={formData.description}
              onChange={handleInputChange}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 min-h-32"
              required
            />
          </div>

          {/* AI Categorization Button */}
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-white font-semibold flex items-center">
                  <span className="mr-2">🤖</span> AI-Powered Categorization
                </h3>
                <p className="text-gray-400 text-sm">Let AI analyze and categorize your book automatically</p>
              </div>
              <Button
                type="button"
                onClick={handleAICategorization}
                disabled={analyzing || !formData.title || !formData.author || !formData.description}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {analyzing ? 'Analyzing...' : 'Analyze Book'}
              </Button>
            </div>
            
            {aiCategories && (
              <div className="mt-4 space-y-2 bg-black/30 p-4 rounded">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Primary Genre:</span>
                    <span className="text-white font-semibold ml-2">{aiCategories.primary_genre}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Audience:</span>
                    <span className="text-white font-semibold ml-2">{aiCategories.target_audience}</span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-400 text-sm">Categories:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {aiCategories.categories.map((cat) => (
                      <span key={`cat-${cat}`} className="px-2 py-1 bg-purple-600/20 text-purple-200 rounded text-xs">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-gray-400 text-sm">Themes:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {aiCategories.themes.map((theme) => (
                      <span key={`theme-${theme}`} className="px-2 py-1 bg-blue-600/20 text-blue-200 rounded text-xs">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Genres */}
          <div className="space-y-2">
            <Label htmlFor="genres" className="text-white text-lg">
              Genres *
            </Label>
            <Input
              id="genres"
              name="genres"
              type="text"
              placeholder="e.g., Fiction, Mystery, Thriller (comma-separated)"
              value={formData.genres}
              onChange={handleInputChange}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
              required
            />
          </div>

          {/* Pages and Year */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pages" className="text-white text-lg">
                Number of Pages *
              </Label>
              <Input
                id="pages"
                name="pages"
                type="number"
                placeholder="320"
                value={formData.pages}
                onChange={handleInputChange}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="publishYear" className="text-white text-lg">
                Publish Year *
              </Label>
              <Input
                id="publishYear"
                name="publishYear"
                type="number"
                placeholder="2024"
                value={formData.publishYear}
                onChange={handleInputChange}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                required
              />
            </div>
          </div>

          {/* Purchase Links (Optional) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amazonLink" className="text-white text-lg">Amazon Link <span className="text-gray-500 text-sm">(Optional)</span></Label>
              <Input
                id="amazonLink"
                name="amazonLink"
                type="url"
                placeholder="https://www.amazon.com/dp/..."
                value={formData.amazonLink}
                onChange={handleInputChange}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                data-testid="amazon-link-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="barnesNobleLink" className="text-white text-lg">Barnes & Noble Link <span className="text-gray-500 text-sm">(Optional)</span></Label>
              <Input
                id="barnesNobleLink"
                name="barnesNobleLink"
                type="url"
                placeholder="https://www.barnesandnoble.com/w/..."
                value={formData.barnesNobleLink}
                onChange={handleInputChange}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                data-testid="barnes-noble-link-input"
              />
            </div>
          </div>

          {/* Author Bio (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="authorBio" className="text-white text-lg">
              Author's Bio <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <Textarea
              id="authorBio"
              name="authorBio"
              placeholder="Write a brief biography about the author..."
              value={formData.authorBio}
              onChange={handleInputChange}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 min-h-24"
              data-testid="author-bio-input"
            />
          </div>

          {/* Author Photo (Optional) */}
          <div className="space-y-2">
            <Label className="text-white text-lg">
              Author's Photo <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <div className="flex items-center gap-4">
              {authorPhotoPreview ? (
                <div className="relative">
                  <img src={authorPhotoPreview} alt="Author" className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/50" />
                  <button
                    type="button"
                    onClick={() => { setAuthorPhoto(null); setAuthorPhotoPreview(null); }}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center border border-white/20">
                  <ImageIcon className="h-6 w-6 text-gray-500" />
                </div>
              )}
              <label className="cursor-pointer bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm px-4 py-2 rounded-lg transition-colors">
                Choose Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setAuthorPhoto(file);
                      setAuthorPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                  className="hidden"
                  data-testid="author-photo-upload-input"
                />
              </label>
            </div>
          </div>

          {/* Series Information (Optional) */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">Series Information <span className="text-gray-500 text-sm font-normal">(Optional)</span></h3>
            <p className="text-gray-400 text-sm">If this book is part of a series, fill in the details below to enable "Next in Series" navigation.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="seriesName" className="text-white">Series Name</Label>
                <Input
                  id="seriesName"
                  name="seriesName"
                  type="text"
                  placeholder='e.g., "Harry Potter", "The Hunger Games"'
                  value={formData.seriesName}
                  onChange={handleInputChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                  data-testid="series-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="seriesOrder" className="text-white">Book Number in Series</Label>
                <Input
                  id="seriesOrder"
                  name="seriesOrder"
                  type="number"
                  min="1"
                  placeholder="e.g., 1, 2, 3..."
                  value={formData.seriesOrder}
                  onChange={handleInputChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                  data-testid="series-order-input"
                />
              </div>
            </div>
          </div>

          {/* Cover Image Upload */}
          <div className="space-y-2">
            <Label className="text-white text-lg">Book Cover Image *</Label>
            <div className="flex items-start space-x-4">
              <div className="flex-1">
                <label
                  htmlFor="cover"
                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-white/20 border-dashed rounded-lg cursor-pointer bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ImageIcon className="w-10 h-10 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-400">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG (MAX. 5MB)</p>
                  </div>
                  <input
                    id="cover"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleCoverChange}
                    required
                  />
                </label>
              </div>
              {coverPreview && (
                <div className="relative">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-32 h-40 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCoverImage(null);
                      setCoverPreview(null);
                    }}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Full Book File Upload */}
          <div className="space-y-2">
            <Label className="text-white text-lg">Full Book File (PDF/EPUB) *</Label>
            <p className="text-gray-400 text-sm mb-2">Upload the complete book for members to read</p>
            <label
              htmlFor="bookFile"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-white/20 border-dashed rounded-lg cursor-pointer bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FileText className="w-10 h-10 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-400">
                  <span className="font-semibold">
                    {bookFile ? bookFile.name : 'Click to upload full book'}
                  </span>
                </p>
                <p className="text-xs text-gray-500">PDF or EPUB format</p>
              </div>
              <input
                id="bookFile"
                type="file"
                className="hidden"
                accept=".pdf,.epub"
                onChange={handleBookFileChange}
                required
              />
            </label>
          </div>

          {/* Book Trailer Upload (Optional) */}
          <div className="space-y-2">
            <Label className="text-white text-lg flex items-center">
              <Video className="h-5 w-5 mr-2 text-red-500" />
              Book Trailer (Optional)
            </Label>
            <p className="text-gray-400 text-sm mb-2">
              Upload a promotional video trailer. <span className="text-red-400 font-semibold">Max 2:30</span>, 250MB, MP4 or WebM format.
            </p>
            
            {!trailerFile ? (
              <label
                htmlFor="trailerFile"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-red-500/30 border-dashed rounded-lg cursor-pointer bg-red-500/5 hover:bg-red-500/10 transition-colors"
                data-testid="trailer-upload-area"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Video className="w-10 h-10 mb-3 text-red-400" />
                  <p className="mb-2 text-sm text-gray-400">
                    <span className="font-semibold text-red-400">Click to upload trailer</span>
                  </p>
                  <p className="text-xs text-gray-500">MP4 or WebM (Max 2:30, 250MB)</p>
                </div>
                <input
                  ref={videoInputRef}
                  id="trailerFile"
                  type="file"
                  className="hidden"
                  accept="video/mp4,video/webm"
                  onChange={handleTrailerChange}
                  data-testid="trailer-file-input"
                />
              </label>
            ) : (
              <div className="border-2 border-green-500/30 rounded-lg p-4 bg-green-500/5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center text-green-400 mb-2">
                      <Video className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Trailer Ready</span>
                    </div>
                    <p className="text-white text-sm">{trailerFile.name}</p>
                    <p className="text-gray-400 text-xs">
                      {(trailerFile.size / 1024 / 1024).toFixed(2)} MB • 
                      Duration: {Math.floor(trailerDuration / 60)}:{String(Math.floor(trailerDuration % 60)).padStart(2, '0')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearTrailer}
                    className="bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {trailerPreview && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    <video
                      src={trailerPreview}
                      controls
                      className="w-full max-h-48 rounded"
                      data-testid="trailer-preview"
                    />
                  </div>
                )}
              </div>
            )}

            {trailerError && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{trailerError}</p>
              </div>
            )}

            {/* OR: External Trailer Link */}
            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-black text-gray-500">OR paste a link</span>
              </div>
            </div>
            <div>
              <input
                type="url"
                placeholder="YouTube or Facebook video link (e.g., https://www.youtube.com/watch?v=...)"
                value={trailerExternalUrl}
                onChange={(e) => setTrailerExternalUrl(e.target.value)}
                className="w-full h-10 px-4 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-500 focus:border-red-500 focus:outline-none"
                data-testid="trailer-external-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">Paste a YouTube or Facebook video link. Loads instantly — no upload needed.</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <Button
              type="submit"
              disabled={loading || !!trailerError}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg transition-all disabled:opacity-50"
              data-testid="upload-book-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {uploadStatus || 'Uploading...'}
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <UploadIcon className="mr-2 h-5 w-5" />
                  Upload Book {trailerFile && '+ Trailer'}
                </span>
              )}
            </Button>
            
            {/* Progress bar for large trailer uploads */}
            {loading && uploadProgress > 0 && (
              <div className="mt-4">
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-red-600 to-red-400 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-sm">
                  <span className="text-gray-400">{uploadStatus}</span>
                  <span className="text-white font-semibold">{uploadProgress}%</span>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadBook;
